/**
 * Survey Builder Form Component
 * Create and edit surveys using templates
 */

import React, { useState, useEffect } from 'react';
import { Save, ArrowLeft, Eye } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { createSurvey, updateSurvey, getSurvey } from '../../services/surveyAPI';
import { getCohorts } from '../../services/cohortAPI';
import TemplateSelector from './TemplateSelector';
import TemplatePreview from './TemplatePreview';
import Toast from './Toast';

const SurveyBuilderForm = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = !!surveyId;

  const [surveyData, setSurveyData] = useState({
    cohort_id: '',
    template_id: '',
    survey_type: 'BASELINE',
    response_window_days: 28,
    reminder_schedule: [7, 14, 21],
    target_recipients: ['SELF', 'MANAGER', 'DIRECT_REPORT', 'ADHOC']
  });

  const [cohorts, setCohorts] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [toast, setToast] = useState(null);
  const [isPrePopulated, setIsPrePopulated] = useState(false);
  const [lockedFields, setLockedFields] = useState({ cohort: false, surveyType: false });

  const surveyTypes = ['BASELINE', 'MIDPOINT', 'FINAL'];

  useEffect(() => {
    loadData();
  }, [surveyId, location.search]);

  const loadData = async () => {
    try {
      setLoading(true);
      const cohortsRes = await getCohorts();
      
      // API returns { success, data: { cohorts }, timestamp }
      const cohortsList = cohortsRes.data?.cohorts || cohortsRes.cohorts || [];
      setCohorts(cohortsList);

      // Check for query parameters (from "Create Survey" button in grouped view)
      const params = new URLSearchParams(location.search);
      const cohortIdParam = params.get('cohortId');
      const surveyTypeParam = params.get('surveyType');
      const copyFromParam = params.get('copyFrom');

      if (surveyId) {
        // Editing existing survey
        const surveyRes = await getSurvey(surveyId);
        const survey = surveyRes.data?.survey || surveyRes.survey;
        
        setSurveyData({
          cohort_id: survey.cohort_id.toString(),
          template_id: survey.template_id || '',
          survey_type: survey.survey_type,
          response_window_days: survey.response_window_days || 28,
          reminder_schedule: survey.reminder_schedule || [7, 14, 21],
          target_recipients: survey.target_recipients || ['SELF', 'MANAGER', 'DIRECT_REPORT', 'ADHOC']
        });

        // Set selected cohort for template filtering
        const cohort = cohortsList.find(c => c.id === survey.cohort_id);
        setSelectedCohort(cohort);
      } else if (cohortIdParam && surveyTypeParam) {
        // Creating new survey with pre-population
        const cohort = cohortsList.find(c => c.id === parseInt(cohortIdParam));
        setSelectedCohort(cohort);
        
        // Lock cohort and survey type fields
        setLockedFields({ cohort: true, surveyType: true });
        
        // Set initial data
        const initialData = {
          cohort_id: cohortIdParam,
          template_id: '',
          survey_type: surveyTypeParam,
          response_window_days: 28,
          reminder_schedule: [7, 14, 21],
          target_recipients: ['SELF', 'MANAGER', 'DIRECT_REPORT', 'ADHOC']
        };

        // If copyFrom is specified, fetch that survey and copy its settings
        if (copyFromParam && cohort) {
          try {
            // Fetch all surveys for this cohort to find the source survey
            const response = await fetch(
              `${process.env.REACT_APP_API_URL || 'https://ybtyyty4e9.execute-api.us-west-2.amazonaws.com/prod/leading'}/api/surveys?cohort_id=${cohortIdParam}`,
              {
                headers: {
                  'Authorization': `Bearer ${await window.getAccessToken()}`,
                  'Content-Type': 'application/json'
                }
              }
            );
            
            if (response.ok) {
              const data = await response.json();
              const surveys = data.data?.surveys || data.surveys || [];
              const sourceSurvey = surveys.find(s => s.survey_type === copyFromParam);
              
              if (sourceSurvey) {
                initialData.template_id = sourceSurvey.template_id || '';
                initialData.response_window_days = sourceSurvey.response_window_days || 28;
                initialData.reminder_schedule = sourceSurvey.reminder_schedule || [7, 14, 21];
                initialData.target_recipients = sourceSurvey.target_recipients || ['SELF', 'MANAGER', 'DIRECT_REPORT', 'ADHOC'];
                setIsPrePopulated(true);
                showToast(`Pre-populated from ${copyFromParam} survey`, 'success');
              }
            }
          } catch (error) {
            console.error('Error fetching source survey:', error);
            // Continue with defaults if fetch fails
          }
        }
        
        setSurveyData(initialData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleCohortChange = (cohortId) => {
    const cohort = cohorts.find(c => c.id === parseInt(cohortId));
    setSelectedCohort(cohort);
    setSurveyData({
      ...surveyData,
      cohort_id: cohortId,
      template_id: '' // Reset template when cohort changes
    });
  };

  const handleTemplateSelect = (templateId) => {
    setSurveyData({
      ...surveyData,
      template_id: templateId
    });
  };

  const toggleRecipient = (type) => {
    const current = surveyData.target_recipients;
    if (current.includes(type)) {
      setSurveyData({
        ...surveyData,
        target_recipients: current.filter(t => t !== type)
      });
    } else {
      setSurveyData({
        ...surveyData,
        target_recipients: [...current, type]
      });
    }
  };

  const handleSave = async () => {
    try {
      if (!surveyData.cohort_id) {
        showToast('Please select a cohort', 'error');
        return;
      }
      if (!surveyData.template_id) {
        showToast('Please select a template', 'error');
        return;
      }
      if (surveyData.target_recipients.length === 0) {
        showToast('Please select at least one target recipient', 'error');
        return;
      }

      setSaving(true);

      const payload = {
        cohort_id: parseInt(surveyData.cohort_id),
        template_id: parseInt(surveyData.template_id),
        survey_type: surveyData.survey_type,
        response_window_days: surveyData.response_window_days,
        reminder_schedule: surveyData.reminder_schedule,
        target_recipients: surveyData.target_recipients
      };

      if (isEditing) {
        await updateSurvey(surveyId, payload);
        showToast('Survey updated successfully!');
      } else {
        await createSurvey(payload);
        showToast('Survey created successfully!');
      }

      // Check if we should return to grouped view
      const params = new URLSearchParams(location.search);
      const returnTo = params.get('returnTo');
      const cohortId = params.get('cohortId') || params.get('expandCohort');
      
      setTimeout(() => {
        if (returnTo === 'grouped' && cohortId) {
          navigate(`/leading/admin/surveys/grouped?expandCohort=${cohortId}`);
        } else {
          navigate('/leading/admin/surveys');
        }
      }, 1500);
    } catch (error) {
      console.error('Error saving survey:', error);
      console.error('Error response data:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to save survey';
      showToast(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <div className="spinner" style={{ margin: '0 auto' }}></div>
          <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <button
            onClick={() => navigate('/leading/admin/surveys')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#374151',
              cursor: 'pointer',
              marginBottom: '1rem',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#e5e7eb'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#f3f4f6'}
          >
            <ArrowLeft size={16} />
            <span>Back to Surveys</span>
          </button>
          <h1 style={{ 
            fontSize: '2rem', 
            fontWeight: '700', 
            color: '#111827',
            marginBottom: '0.5rem'
          }}>
            {isEditing ? 'Edit Survey' : 'Create New Survey'}
          </h1>
          <p style={{ color: '#6b7280', fontSize: '1rem', margin: 0 }}>
            Configure leadership competency survey
          </p>
        </div>

        <div style={{ display: 'grid', gap: '2rem' }}>
          {/* Survey Configuration */}
          <div style={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '700', color: '#111827', marginBottom: '1.5rem' }}>
              Survey Configuration
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              {/* Cohort Selection */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  Cohort *
                </label>
                <select
                  value={surveyData.cohort_id}
                  onChange={(e) => handleCohortChange(e.target.value)}
                  disabled={isEditing || lockedFields.cohort}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    background: (isEditing || lockedFields.cohort) ? '#f3f4f6' : '#ffffff',
                    cursor: (isEditing || lockedFields.cohort) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <option value="">Select cohort...</option>
                  {cohorts
                    .filter(cohort => cohort.status === 'DRAFT' || cohort.status === 'ACTIVE')
                    .map(cohort => (
                      <option key={cohort.id} value={cohort.id}>
                        {cohort.name} ({cohort.status})
                      </option>
                    ))}
                </select>
                {(isEditing || lockedFields.cohort) && (
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', margin: '0.25rem 0 0 0' }}>
                    {isEditing ? 'Cohort cannot be changed when editing' : 'Pre-selected cohort'}
                  </p>
                )}
              </div>

              {/* Survey Type */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  Survey Type *
                </label>
                <select
                  value={surveyData.survey_type}
                  onChange={(e) => setSurveyData({ ...surveyData, survey_type: e.target.value })}
                  disabled={lockedFields.surveyType}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    background: lockedFields.surveyType ? '#f3f4f6' : '#ffffff',
                    cursor: lockedFields.surveyType ? 'not-allowed' : 'pointer'
                  }}
                >
                  {surveyTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Response Window */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '0.5rem'
                }}>
                  Response Window (days)
                </label>
                <input
                  type="number"
                  value={surveyData.response_window_days}
                  onChange={(e) => setSurveyData({ ...surveyData, response_window_days: parseInt(e.target.value) })}
                  min="1"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
            </div>

            {/* Template Selection */}
            {surveyData.cohort_id && selectedCohort && (
              <div style={{ marginTop: '1.5rem' }}>
                <TemplateSelector
                  programLevel={selectedCohort.program_level}
                  selectedTemplateId={surveyData.template_id}
                  onSelect={handleTemplateSelect}
                  disabled={isEditing}
                />
                {isEditing && (
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', margin: '0.5rem 0 0 0' }}>
                    Template cannot be changed when editing
                  </p>
                )}
              </div>
            )}

            {/* Target Recipients */}
            <div style={{ marginTop: '1.5rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.75rem'
              }}>
                Target Recipients
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
                {[
                  { key: 'SELF', label: 'Self' },
                  { key: 'MANAGER', label: 'Manager' },
                  { key: 'DIRECT_REPORT', label: 'Direct Reports' },
                  { key: 'ADHOC', label: 'Adhoc' }
                ].map(({ key, label }) => (
                  <label
                    key={key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem',
                      background: surveyData.target_recipients.includes(key) ? '#dbeafe' : '#f9fafb',
                      border: `1px solid ${surveyData.target_recipients.includes(key) ? '#93c5fd' : '#e5e7eb'}`,
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={surveyData.target_recipients.includes(key)}
                      onChange={() => toggleRecipient(key)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ 
                      fontSize: '0.875rem', 
                      fontWeight: '500',
                      color: surveyData.target_recipients.includes(key) ? '#1e40af' : '#374151'
                    }}>
                      {label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Template Preview Section */}
          {surveyData.template_id && (
            <div style={{
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '700', color: '#111827', margin: 0 }}>
                  Survey Questions
                </h3>
                <button
                  onClick={() => setShowPreview(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    background: 'linear-gradient(135deg, #6366f1 0%, #5b21b6 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <Eye size={16} />
                  <span>Preview Questions</span>
                </button>
              </div>

              <div style={{
                padding: '1.5rem',
                background: '#f0fdf4',
                border: '1px solid #86efac',
                borderRadius: '0.5rem',
                textAlign: 'center'
              }}>
                <p style={{ color: '#047857', fontSize: '0.875rem', margin: 0 }}>
                  Questions will be automatically generated from the selected template when the survey is sent.
                  Click "Preview Questions" to see how they will appear for each rater type.
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              onClick={() => navigate('/leading/admin/surveys')}
              disabled={saving}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#ffffff',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!saving) e.currentTarget.style.background = '#f9fafb';
              }}
              onMouseLeave={(e) => {
                if (!saving) e.currentTarget.style.background = '#ffffff';
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: saving ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: saving ? 'none' : '0 4px 12px rgba(16, 185, 129, 0.25)'
              }}
              onMouseEnter={(e) => {
                if (!saving) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.35)';
                }
              }}
              onMouseLeave={(e) => {
                if (!saving) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.25)';
                }
              }}
            >
              {saving ? (
                <>
                  <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save size={18} />
                  <span>{isEditing ? 'Update Survey' : 'Create Survey'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Template Preview Modal */}
      <TemplatePreview
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        templateId={surveyData.template_id}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </AdminLayout>
  );
};

export default SurveyBuilderForm;
